import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { filter } from 'rxjs';
import { ContentfulService } from './contentful.service';
import { EnvironmentVariablesService } from './environment-variables.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class TimeoutService {
  private idle = inject(Idle);
  private keepalive = inject(Keepalive);
  private cfService = inject(ContentfulService);
  private evService = inject(EnvironmentVariablesService);
  private userService = inject(UserService);
  private router = inject(Router);

  sessiontimeout = false;
  private sessionPageContentState = signal<string | null>(null);
  readonly sessionPageContent = this.sessionPageContentState.asReadonly();
  public sessionPageContent$ = toObservable(this.sessionPageContentState);
  sessionTimeLeft!: number;

  constructor() {
    const idle = this.idle;
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      if (this.userService) {
        if (!this.sessionPageContentState()) {
          this.cfService.fetchCommonAssets('session', '').subscribe(result => {
            this.sessionPageContentState.set(JSON.stringify((result as { data: { sessionTimeoutSection: unknown } }).data.sessionTimeoutSection));
          });
        }
        idle.watch();
        this.sessiontimeout = false;
      }
      else {
        idle.stop();
      }
    });
  }

  setIdleConfigs() {
    // how long can they be inactive before considered idle, in seconds
    this.idle.setIdle(this.evService.sessionStandAloneTime);
    this.idle.setTimeout(60); // how long can they be idle before considered timed out, in seconds
    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.onIdleStart.subscribe(() => {
      this.idle.clearInterrupts();
      if (this.userService.authenticated()) {
        this.sessiontimeout = true;
      }
    });

    this.idle.onTimeoutWarning.subscribe((countdown) => {
      this.sessionTimeLeft = countdown;
    });

    this.idle.onTimeout.subscribe(() => {
      if (this.userService.authenticated()) {
        this.sessiontimeout = false;
        this.userService.logoutApp();
      }
    });

    this.keepalive.interval(15); // will ping at this interval while not idle, in seconds
  }

  resetSessionIdleData() {
    this.userService.checkIfSessionExistsAndLogoutIfExpired();
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.sessiontimeout = false;
    this.idle.watch();
  }

  sessionContinueClick() {
    this.resetSessionIdleData();
    localStorage.setItem('session-continue', 'session continued' + Math.random());
  }
}
